import React, { useEffect, useState } from 'react';
import { Check, X } from 'lucide-react';
import { passwordService } from '@/services/password.service';

interface PasswordStrengthIndicatorProps {
  password: string;
  onStrengthChange?: (strength: 'weak' | 'medium' | 'strong' | 'very_strong') => void;
}

export const PasswordStrengthIndicator: React.FC<PasswordStrengthIndicatorProps> = ({
  password,
  onStrengthChange
}) => {
  const [strength, setStrength] = useState<{
    strength: 'weak' | 'medium' | 'strong' | 'very_strong';
    score: number;
    requirements: {
      min_length: boolean;
      has_uppercase: boolean;
      has_lowercase: boolean;
      has_number: boolean;
      has_special: boolean;
    };
    suggestions: string[];
  } | null>(null);

  useEffect(() => {
    if (!password) {
      setStrength(null);
      return;
    }

    const checkStrength = async () => {
      const result = await passwordService.checkPasswordStrength(password);
      // Ensure suggestions array exists
      const safeResult = {
        ...result,
        suggestions: result.suggestions || []
      };
      setStrength(safeResult);
      onStrengthChange?.(result.strength);
    };

    const debounceTimer = setTimeout(checkStrength, 300);
    return () => clearTimeout(debounceTimer);
  }, [password, onStrengthChange]);

  if (!password || !strength) return null;

  const getStrengthColor = () => {
    switch (strength.strength) {
      case 'weak':
        return 'bg-red-500';
      case 'medium':
        return 'bg-yellow-500';
      case 'strong':
        return 'bg-green-500';
      case 'very_strong':
        return 'bg-green-600';
      default:
        return 'bg-gray-300';
    }
  };

  const getStrengthText = () => {
    switch (strength.strength) {
      case 'weak':
        return 'Weak';
      case 'medium':
        return 'Fair';
      case 'strong':
        return 'Strong';
      case 'very_strong':
        return 'Very Strong';
      default:
        return '';
    }
  };

  const getStrengthTextColor = () => {
    switch (strength.strength) {
      case 'weak':
        return 'text-red-600';
      case 'medium':
        return 'text-yellow-600';
      case 'strong':
        return 'text-green-600';
      case 'very_strong':
        return 'text-green-700';
      default:
        return 'text-gray-600';
    }
  };

  return (
    <div className="mt-2 space-y-2">
      {/* Strength Bar */}
      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-600">Password Strength</span>
          <span className={`text-xs font-medium ${getStrengthTextColor()}`}>
            {getStrengthText()}
          </span>
        </div>
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-300 ${getStrengthColor()}`}
            style={{ width: `${strength.score}%` }}
          />
        </div>
      </div>

      {/* Requirements */}
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          {strength.requirements.min_length ? (
            <Check className="w-3 h-3 text-green-500" />
          ) : (
            <X className="w-3 h-3 text-gray-400" />
          )}
          <span className={`text-xs ${strength.requirements.min_length ? 'text-green-700' : 'text-gray-500'}`}>
            At least 8 characters
          </span>
        </div>
        
        <div className="flex items-center gap-2">
          {strength.requirements.has_uppercase ? (
            <Check className="w-3 h-3 text-green-500" />
          ) : (
            <X className="w-3 h-3 text-gray-400" />
          )}
          <span className={`text-xs ${strength.requirements.has_uppercase ? 'text-green-700' : 'text-gray-500'}`}>
            One uppercase letter
          </span>
        </div>
        
        <div className="flex items-center gap-2">
          {strength.requirements.has_lowercase ? (
            <Check className="w-3 h-3 text-green-500" />
          ) : (
            <X className="w-3 h-3 text-gray-400" />
          )}
          <span className={`text-xs ${strength.requirements.has_lowercase ? 'text-green-700' : 'text-gray-500'}`}>
            One lowercase letter
          </span>
        </div>
        
        <div className="flex items-center gap-2">
          {strength.requirements.has_number ? (
            <Check className="w-3 h-3 text-green-500" />
          ) : (
            <X className="w-3 h-3 text-gray-400" />
          )}
          <span className={`text-xs ${strength.requirements.has_number ? 'text-green-700' : 'text-gray-500'}`}>
            One number
          </span>
        </div>
        
        <div className="flex items-center gap-2">
          {strength.requirements.has_special ? (
            <Check className="w-3 h-3 text-green-500" />
          ) : (
            <X className="w-3 h-3 text-gray-400" />
          )}
          <span className={`text-xs ${strength.requirements.has_special ? 'text-green-700' : 'text-gray-500'}`}>
            One special character (recommended)
          </span>
        </div>
      </div>

      {/* Suggestions */}
      {strength.suggestions && strength.suggestions.length > 0 && strength.strength !== 'very_strong' && (
        <div className="mt-2">
          <p className="text-xs text-gray-600 mb-1">Suggestions:</p>
          <ul className="space-y-1">
            {strength.suggestions.slice(0, 2).map((suggestion, index) => (
              <li key={index} className="text-xs text-gray-500 flex items-start gap-1">
                <span>•</span>
                <span>{suggestion}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default PasswordStrengthIndicator;